<script setup>
import AdminLayout from "../Layouts/AdminLayout.vue";
import Button from "primevue/button";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { ref, computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { Inertia } from "@inertiajs/inertia";
import CustomerSelectDialog from "../Components/CustomerSelectDialog.vue";
import ComposeMessageDialog from "../Components/ComposeMessageDialog.vue";
import Dropdown from "primevue/dropdown";

const props = defineProps({
    customers: {
        type: Array,
        default: () => []
    },
    filter: {
        type: String,
        default: 'active'
    },
    gender: {
        type: String,
        default: 'all'
    },
    isSuperAdmin: {
        type: Boolean,
        default: false
    },
    companyOptions: {
        type: Array,
        default: () => []
    },
    success: {
        type: String,
        default: null
    }
});

const toast = useToast();

const customerSearch = ref("");
const showCustomerSelectDialog = ref(false);
const showComposeDialog = ref(false);
const selectedRecipients = ref([]);
const selectedFilter = ref(props.filter);
const selectedGender = ref(props.gender);
const selectedCompany = ref(null);

const filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'All Active', value: 'active' }
];

const genderOptions = [
    { label: 'All Genders', value: 'all' },
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' }
];

// Check for success message on component mount
onMounted(() => {
    if (props.success) {
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: props.success,
            life: 3000
        });
    }
});

const filteredCustomers = computed(() => {
    if (!customerSearch.value) return props.customers;
    
    return props.customers.filter(customer => 
        customer.name.toLowerCase().includes(customerSearch.value.toLowerCase()) ||
        customer.email.toLowerCase().includes(customerSearch.value.toLowerCase())
    );
});


const handleSendToIndividual = (customer) => {
    selectedRecipients.value = [customer];
    selectedUsingSelectAll.value = false;
    showComposeDialog.value = true;
};

const handleSendToMany = () => {
    showCustomerSelectDialog.value = true;
};

const selectedUsingSelectAll = ref(false);

const handleCustomersSelected = (customers, isSelectAll) => {
    selectedRecipients.value = customers;
    selectedUsingSelectAll.value = isSelectAll;
    showCustomerSelectDialog.value = false;
    showComposeDialog.value = true;
};

const handleMessageSent = () => {
    showComposeDialog.value = false;
    selectedRecipients.value = [];
    selectedUsingSelectAll.value = false;
    // TODO: Refresh history log
};

const handleTabChange = (event) => {
    // Index 0: Customers (Current)
    // Index 1: Birthdays
    // Index 2: History Log
    
    if (event.index === 1) {
        Inertia.visit('/admin/notifications/birthdays');
    } else if (event.index === 2) {
        Inertia.visit('/admin/notifications/history');
    }
};

const handleFilterChange = () => {
    const params = {
        filter: selectedFilter.value,
        gender: selectedGender.value,
    };

    if (props.isSuperAdmin) {
        params.company_id = selectedCompany.value;
    }

    Inertia.get('/admin/notifications', params, {
        preserveState: true,
        preserveScroll: true
    });
};
</script>

<template>
    <AdminLayout>
        <div class="p-6">
            <div class="flex justify-between items-center mb-6">
                <h1 class="text-2xl font-semibold">Notifications Dashboard</h1>
                <Button 
                    label="Send to Many" 
                    icon="pi pi-send" 
                    @click="handleSendToMany"
                    severity="primary"
                />
            </div>

            <TabView @tab-change="handleTabChange">
                <TabPanel header="Customers">
                    <div class="mb-4 flex gap-4 items-end">
                        <div class="flex-grow">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Suche</label>
                            <IconField iconPosition="left" class="w-full">
                                <InputIcon class="pi pi-search" />
                                <InputText 
                                    v-model="customerSearch" 
                                    placeholder="Search customers..." 
                                    class="w-full !h-[42px]"
                                />
                            </IconField>
                        </div>
                        
                        <div class="w-40 shrink-0">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Status</label>
                            <Dropdown 
                                v-model="selectedFilter" 
                                :options="filterOptions" 
                                optionLabel="label" 
                                optionValue="value"
                                @change="handleFilterChange"
                                class="w-full !h-[42px] flex items-center"
                            />
                        </div>

                        <div class="w-44 shrink-0">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Geschlecht</label>
                            <Dropdown
                                v-model="selectedGender"
                                :options="genderOptions"
                                optionLabel="label"
                                optionValue="value"
                                @change="handleFilterChange"
                                class="w-full !h-[42px] flex items-center"
                            />
                        </div>

                        <div v-if="isSuperAdmin" class="w-64 shrink-0">
                            <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                            <Dropdown
                                v-model="selectedCompany"
                                :options="companyOptions"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Alle Unternehmen"
                                @change="handleFilterChange"
                                class="w-full !h-[42px] flex items-center"
                                showClear
                                filter
                            />
                        </div>
                    </div>

                    <DataTable 
                        :value="filteredCustomers" 
                        dataKey="id"
                        :rows="10"
                        :paginator="true"
                        responsiveLayout="scroll"
                        class="mt-4"
                    >
                        <Column field="name" header="Name">
                            <template #body="{ data }">
                                <div class="flex items-center gap-3">
                                    <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                        <i class="pi pi-user text-gray-600"></i>
                                    </div>
                                    <div>
                                        <div class="font-medium">{{ data.name }}</div>
                                        <div class="text-sm text-gray-500">{{ data.email }}</div>
                                    </div>
                                </div>
                            </template>
                        </Column>
                        <Column field="lastActive" header="Last Active"></Column>
                        <Column header="Action" style="width: 100px">
                            <template #body="{ data }">
                                <Button 
                                    label="Send" 
                                    size="small"
                                    @click="handleSendToIndividual(data)"
                                />
                            </template>
                        </Column>
                    </DataTable>
                </TabPanel>

                <TabPanel header="Birthdays">
                    <!-- This tab will redirect to birthdays page when clicked -->
                </TabPanel>

                <TabPanel header="History Log">
                    <!-- This tab will redirect to history page when clicked -->
                </TabPanel>
            </TabView>
        </div>

        <CustomerSelectDialog 
            v-model:visible="showCustomerSelectDialog"
            :customers="customers"
            :currentFilter="selectedFilter"
            @select="handleCustomersSelected"
        />

        <ComposeMessageDialog 
            v-model:visible="showComposeDialog"
            :recipients="selectedRecipients"
            :currentFilter="selectedFilter"
            :currentGender="selectedGender"
            :totalCustomers="customers.length"
            :sendToAll="selectedUsingSelectAll"
            @sent="handleMessageSent"
        />
    </AdminLayout>
</template>